import type { SourceGoalHistory } from '../../services/api/types.gen';
import { formatDateForDisplay } from '../../utils/dateUtils';

/** Evidence only. Never passed to progress editors or completion controls. */
export default function SourceHistory({
  entries,
}: {
  entries?: SourceGoalHistory[];
}) {
  if (!entries?.length) return null;
  const ordered = [...entries].sort(
    (a, b) =>
      (b.visit_date ?? '').localeCompare(a.visit_date ?? '') ||
      b.history_key - a.history_key
  );
  return (
    <details className='rounded-md border border-border/60 bg-muted/20 p-3 text-xs'>
      <summary className='cursor-pointer font-medium'>
        Source history · External EMR ({entries.length})
      </summary>
      <p className='mt-2 text-muted-foreground'>
        Copied source evidence, preserved read-only. These entries do not record
        new progress or complete interventions in Roger. Records with preserved
        source history cannot be deleted.
      </p>
      <ol className='mt-3 space-y-3'>
        {ordered.map(entry => (
          <li
            key={`${entry.kind}:${entry.history_key}`}
            className='border-t border-border/60 pt-2'
          >
            <div className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
              <span className='font-medium'>
                {entry.visit_date
                  ? formatDateForDisplay(entry.visit_date)
                  : 'Source visit date unavailable'}
              </span>
              <span>
                {entry.score ?? entry.result ?? 'Performed in External EMR'}
              </span>
              {entry.inherited && (
                <span className='text-muted-foreground'>
                  Inherited in Roger
                </span>
              )}
            </div>
            {entry.clinician_name && (
              <p className='mt-1'>{entry.clinician_name}</p>
            )}
            {entry.kind === 'intervention' && (
              <p className='mt-1'>
                Performed on:{' '}
                {[
                  entry.performed_on_patient ? 'patient' : null,
                  entry.performed_on_caregiver ? 'caregiver' : null,
                ]
                  .filter(Boolean)
                  .join(' and ') || 'not specified in source'}
              </p>
            )}
            {entry.note && (
              <p className='mt-1 whitespace-pre-wrap [overflow-wrap:anywhere]'>
                {entry.note}
              </p>
            )}
            <p className='mt-1 text-muted-foreground'>
              Observed on External EMR goal {entry.source_goal_key ?? 'unknown'}
              {entry.source_intervention_key != null &&
                ` · intervention ${entry.source_intervention_key}`}
              {` · task ${entry.task_key}`}
              {entry.source_episode_key &&
                ` · episode ${entry.source_episode_key}`}
            </p>
            <p className='text-muted-foreground'>
              {entry.captured_at
                ? `Source captured ${formatDateForDisplay(entry.captured_at)}`
                : 'Source capture date unavailable'}
            </p>
          </li>
        ))}
      </ol>
    </details>
  );
}
